// /brand-imagery — "Brand Imagery". The customer picks ONE of their own scraped
// brand photos and we generate ONE on-brand variation ("more of the same" — same
// look/subject/lighting, naturally varied), with or without the product, an
// optional camera-angle override, and optional add/replace on-image copy.
//
// Screen 2 mirrors Product Shot exactly: a full-panel loading takeover
// (StudioGeneratingTakeover, copied verbatim from /appv3) under one persistent
// header, then the SHARED CreativeEditor (embedded) in the same panel container.
// Back returns to the picker with the previous selection intact.
// Backend: routes/brand_imagery.py (/api/brand-imagery/generate).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import CreativeEditor from '@/components/feature/CreativeEditor';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';
import {
  getBrandImagery,
  scrapeBrandImagery,
  generateBrandImagery,
  type BrandImage,
} from '@/lib/api';

const CREDITS = 1;
const TEXT_MAX = 120;

// The vetted camera-angle set (same values the Studio "Framing" filter uses;
// backend maps each to its _CD_FRAMING directive). '' = Auto (match reference).
const CAMERA_ANGLES = [
  'Close-up', 'Eye-level', 'Side-on', 'Three-quarter', 'Low-hero', 'Top-down',
] as const;
type CameraAngle = '' | (typeof CAMERA_ANGLES)[number];

// Same white-halo chrome every Studio panel uses (Product Shot / Model Shot / UGC).
const PANEL = 'rounded-2xl shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)]';
// Product Shot's render-flow panel: fills the viewport under the sticky header.
const FIT_PANEL_H = 'h-[calc(100vh-128px)] min-h-[520px] max-h-[860px]';
const LOADING_PANEL = 'rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)]';

// ─────────────────────────────────────────────────────────────────────────────
// StudioGeneratingTakeover — copied VERBATIM from /appv3 (Product Shot) so the
// loading screen is pixel-identical. Honest exponential progress capped at 92%.
// ─────────────────────────────────────────────────────────────────────────────
function StudioGeneratingTakeover({ frameCount }: { frameCount: number }) {
  const isCarousel = frameCount > 1;
  const expectedMs = isCarousel ? 70_000 : 18_000;
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const t0 = Date.now();
    const id = window.setInterval(() => setElapsedMs(Date.now() - t0), 200);
    return () => window.clearInterval(id);
  }, []);

  const tau = expectedMs / Math.log(5);
  const progress = Math.min(92, 92 * (1 - Math.exp(-elapsedMs / tau)));
  const progressPct = Math.round(progress);

  const sparkles = [
    { top: '12%', left: '18%', size: 9, delay: '0s', dur: '3.2s' },
    { top: '22%', left: '80%', size: 7, delay: '0.6s', dur: '2.8s' },
    { top: '58%', left: '10%', size: 11, delay: '1.1s', dur: '3.6s' },
    { top: '72%', left: '86%', size: 8, delay: '0.3s', dur: '3.0s' },
    { top: '38%', left: '92%', size: 6, delay: '1.4s', dur: '2.5s' },
    { top: '86%', left: '40%', size: 10, delay: '0.9s', dur: '3.4s' },
  ];

  return (
    <div className="relative flex-1 min-h-0 overflow-hidden">
      {/* Drifting gradient backdrop */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
          backgroundSize: '300% 300%',
          animation: 'stk-bgshift 8s ease-in-out infinite',
        }}
      />
      {/* Shimmer sweep */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
          backgroundSize: '200% 100%',
          animation: 'stk-shimmer 2.4s linear infinite',
        }}
      />
      {/* Floating sparkle particles */}
      <div className="absolute inset-0 pointer-events-none">
        {sparkles.map((p, i) => (
          <i
            key={i}
            className="ri-sparkling-2-fill text-primary absolute"
            style={{
              top: p.top,
              left: p.left,
              fontSize: `${p.size * 2}px`,
              animation: `stk-float ${p.dur} ease-in-out ${p.delay} infinite, stk-twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
              opacity: 0.7,
            }}
          />
        ))}
      </div>
      {/* Center cluster — ring + copy */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-8 text-center">
        <div className="relative w-28 h-28">
          <svg className="w-28 h-28 -rotate-90" viewBox="0 0 112 112">
            <defs>
              <linearGradient id="stk-ring" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="50%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#38bdf8" />
              </linearGradient>
            </defs>
            <circle cx="56" cy="56" r="48" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="8" />
            <circle
              cx="56" cy="56" r="48" fill="none"
              stroke="url(#stk-ring)" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 48}
              strokeDashoffset={2 * Math.PI * 48 * (1 - progress / 100)}
              style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <i
              className="ri-sparkling-2-fill text-primary text-3xl"
              style={{ animation: 'stk-spin 4s linear infinite, stk-twinkle 1.6s ease-in-out infinite' }}
            />
          </div>
        </div>
        <div>
          <p className="font-display font-bold text-dark text-2xl tabular-nums">{progressPct}%</p>
          <p className="text-base font-bold text-dark mt-1.5">
            AI is crafting your ad…
          </p>
          <p className="text-sm text-dark-muted mt-1 max-w-[320px] mx-auto leading-relaxed">
            Controls are locked while we render — usually
            {isCarousel ? ' about a minute for 4 frames.' : ' around 20 seconds.'}
          </p>
        </div>
      </div>
      <style>{`
        @keyframes stk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes stk-bgshift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes stk-float   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes stk-twinkle { 0%,100% { opacity: 0.35; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.15); } }
        @keyframes stk-spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// Camera-angle pill styling.
function anglePillClass(active: boolean): string {
  return (
    'px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer border ' +
    'disabled:opacity-50 disabled:cursor-not-allowed ' +
    (active
      ? 'bg-primary text-white border-primary shadow-sm shadow-primary/25'
      : 'bg-white text-dark border-surface-dark/40 hover:border-primary hover:text-primary')
  );
}

export default function BrandImageryPage() {
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const { balance, refresh: refreshCredits } = useCredits();

  const [images, setImages] = useState<BrandImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [withProduct, setWithProduct] = useState(true);
  const [cameraAngle, setCameraAngle] = useState<CameraAngle>('');
  const [text, setText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [postId, setPostId] = useState('');   // set → the editor takes over
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingImages(true);
    getBrandImagery()
      .then((res) => { if (!cancelled) setImages(res.images.filter((i) => i.kept)); })
      .catch(() => { if (!cancelled) setImages([]); })
      .finally(() => { if (!cancelled) setLoadingImages(false); });
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  const selected = useMemo(
    () => images.find((i) => i.id === selectedId) ?? null,
    [images, selectedId],
  );

  const lowBalance = (balance ?? 0) < CREDITS;
  const canGenerate = !!selectedId && !generating;
  const renderFlowOpen = generating || !!postId;   // Screen 2 (loading or editor)

  const textLabel = selected?.hasText ? 'Change the text on this image' : 'Add text (optional)';
  const textPlaceholder = selected?.hasText
    ? 'Type new copy to replace the existing text…'
    : 'Type a short line of on-brand copy…';
  const textHint = selected?.hasText
    ? 'Leave blank to keep the existing text as-is.'
    : 'Leave blank for clean imagery, no text.';

  const handleFetchImages = async () => {
    setScraping(true);
    setError('');
    try {
      const res = await scrapeBrandImagery();
      setImages(res.images.filter((i) => i.kept));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't fetch brand images.");
    } finally {
      setScraping(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedId) return;
    if (lowBalance) { navigate('/checkout'); return; }
    setGenerating(true);
    setError('');
    try {
      const res = await generateBrandImagery(selectedId, withProduct, text, '1:1', cameraAngle);
      if (!res.post_id) throw new Error('Generation failed. Please try again.');
      void refreshCredits();
      setPostId(res.post_id);   // hand off to the editor
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Back from the render flow (loading or editor) → picker, selection intact.
  const handleBackToPicker = () => {
    setPostId('');
    setGenerating(false);
    setError('');
  };

  return (
    <AppLayoutV2>
      {/* ── Persistent sticky header — one header across picker → loading → editor ── */}
      <div className="sticky top-0 z-30 bg-white border-b border-surface-dark/40 shadow-[0_2px_14px_-6px_rgba(15,15,30,0.18)]">
        <div className="w-full max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-3">
          {renderFlowOpen && (
            <button
              type="button"
              onClick={handleBackToPicker}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-dark hover:bg-surface-dark/40 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-arrow-left-line text-lg" />
              <span className="hidden sm:inline text-sm font-medium">Back to brand images</span>
            </button>
          )}
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center shadow-md shadow-primary/25">
            <i className="ri-gallery-line text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-dark truncate leading-tight">
              Brand Imagery
            </h1>
            {renderFlowOpen ? (
              <p className="text-xs text-dark-muted truncate">
                {(activeBrand?.name || 'Brand variation')} · 1:1
              </p>
            ) : (
              <p className="hidden sm:block text-xs text-dark-muted truncate">
                Pick a brand photo, tweak the copy, and generate a new shot in the same style.
              </p>
            )}
          </div>
        </div>
      </div>

      {renderFlowOpen ? (
        /* ═══════════ SCREEN 2 — loading takeover, then the shared editor ═══════════ */
        <div className="w-full max-w-[1500px] mx-auto px-6 pt-6 pb-8">
          <div className={`relative ${FIT_PANEL_H} ${LOADING_PANEL} flex flex-col overflow-hidden`}>
            {generating ? (
              <StudioGeneratingTakeover frameCount={1} />
            ) : (
              <CreativeEditor
                initialPostId={postId}
                embedded
                backLabel="Back to brand images"
                onBack={handleBackToPicker}
                showDelete={false}
              />
            )}
          </div>
        </div>
      ) : (
        /* ═══════════════════════ SCREEN 1 — brand-image picker ═══════════════════════ */
        <div className="w-full max-w-[1500px] mx-auto px-6 pt-6 pb-8">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,589px)_minmax(0,843px)] gap-5 items-stretch xl:h-[800px]">
            {/* ============ LEFT — brand-image picker ============ */}
            <div className={`${PANEL} bg-white flex flex-col overflow-hidden min-h-[520px] xl:h-full`}>
              <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark">
                  Select a brand image
                </h2>
                {images.length > 0 && (
                  <button
                    onClick={handleFetchImages}
                    disabled={scraping}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dark-muted/40 hover:border-primary hover:text-primary text-dark text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                  >
                    {scraping
                      ? <><i className="ri-loader-4-line animate-spin" /> Fetching…</>
                      : <><i className="ri-refresh-line" /> Fetch more</>}
                  </button>
                )}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-5 pr-4 pb-5">
                {loadingImages ? (
                  <p className="text-sm text-dark-muted py-8 text-center">Loading your brand images…</p>
                ) : images.length === 0 ? (
                  <div className="py-10 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
                      <i className="ri-image-add-line text-2xl" />
                    </div>
                    <p className="text-dark-muted text-sm mb-4 max-w-[26ch] mx-auto">
                      No brand images yet. Fetch them from your website, then generate more in the same style.
                    </p>
                    <button
                      onClick={handleFetchImages}
                      disabled={scraping}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50"
                    >
                      {scraping
                        ? <><i className="ri-loader-4-line animate-spin" /> Fetching brand images…</>
                        : <><i className="ri-download-cloud-2-line" /> Fetch brand images</>}
                    </button>
                  </div>
                ) : (
                  // Bigger tiles: ~3 columns (matches Product Shot's template grid).
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {images.map((img) => {
                      const isSelected = img.id === selectedId;
                      return (
                        <button
                          key={img.id}
                          onClick={() => setSelectedId(img.id)}
                          title={img.hasText ? 'Has on-image text' : 'Use this image'}
                          className="group flex flex-col gap-1 text-left cursor-pointer"
                        >
                          <div className={`
                            relative aspect-square rounded-xl overflow-hidden
                            border-2 transition-all bg-surface/40
                            ${isSelected
                              ? 'border-primary shadow-md shadow-primary/25 ring-2 ring-primary/30'
                              : 'border-surface-dark/30 group-hover:border-primary/60 group-hover:shadow-md group-hover:shadow-primary/15'}
                          `}>
                            {img.url ? (
                              <img
                                src={img.url}
                                alt="Brand"
                                loading="lazy"
                                draggable={false}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-dark-muted/40">
                                <i className="ri-image-line text-3xl" />
                              </div>
                            )}
                            {img.hasText && (
                              <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-md bg-black/55 text-white text-[10px] font-bold leading-none">
                                TEXT
                              </span>
                            )}
                            {isSelected && (
                              <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                                <i className="ri-check-line text-sm" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ============ RIGHT — preview + controls + generate ============ */}
            <div className={`${PANEL} overflow-hidden flex flex-col min-h-[520px] xl:h-full bg-gradient-to-br from-surface to-surface-dark/40`}>
              <div className="flex-1 min-h-0 flex items-center justify-center p-4">
                {selected?.url ? (
                  <img
                    src={selected.url}
                    alt="Selected brand image"
                    className="max-h-full max-w-full object-contain rounded-xl border border-surface-dark/20 shadow-lg shadow-dark/10"
                  />
                ) : (
                  <div className="text-center px-6">
                    <div className="w-16 h-16 rounded-2xl bg-white/70 text-dark-muted flex items-center justify-center mx-auto mb-4">
                      <i className="ri-gallery-line text-3xl" />
                    </div>
                    <p className="text-dark font-semibold">Pick a brand image to start</p>
                    <p className="text-sm text-dark-muted mt-1">
                      Choose one on the left, tweak the copy, then Generate.
                    </p>
                  </div>
                )}
              </div>

              {/* controls */}
              <div className="border-t border-white/60 bg-white/80 backdrop-blur-sm px-5 py-4 flex-shrink-0 space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold uppercase tracking-[0.08em] text-dark">
                      {textLabel}
                    </label>
                    <span className="text-[11px] text-dark-muted">{text.length}/{TEXT_MAX}</span>
                  </div>
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value.slice(0, TEXT_MAX))}
                    placeholder={textPlaceholder}
                    disabled={!selected}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-dark-muted/40 text-sm bg-white focus:outline-none focus:border-primary disabled:opacity-50 disabled:bg-surface/40"
                  />
                  <p className="text-[11px] text-dark-muted mt-1">{textHint}</p>
                </div>

                <div className="flex items-center gap-2 p-1 rounded-full bg-surface/60 border border-surface-dark/30">
                  <button
                    onClick={() => setWithProduct(true)}
                    className={`flex-1 h-9 rounded-full text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer ${
                      withProduct ? 'bg-primary text-white shadow-md shadow-primary/25' : 'text-dark-muted hover:text-dark'
                    }`}
                  >
                    <i className="ri-shopping-bag-3-line mr-1" /> With product
                  </button>
                  <button
                    onClick={() => setWithProduct(false)}
                    className={`flex-1 h-9 rounded-full text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer ${
                      !withProduct ? 'bg-primary text-white shadow-md shadow-primary/25' : 'text-dark-muted hover:text-dark'
                    }`}
                  >
                    <i className="ri-landscape-line mr-1" /> Scene only
                  </button>
                </div>

                {/* Camera angle — vetted set (labeled pills). '' = Auto. */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.08em] text-dark block mb-1.5">
                    Camera angle
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setCameraAngle('')}
                      className={anglePillClass(cameraAngle === '')}
                    >
                      Auto
                    </button>
                    {CAMERA_ANGLES.map((a) => (
                      <button
                        key={a}
                        onClick={() => setCameraAngle(a)}
                        className={anglePillClass(cameraAngle === a)}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-dark-muted mt-1">
                    Auto keeps the reference's framing. Pick an angle to re-shoot it from a new perspective.
                  </p>
                </div>

                {error && (
                  <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-base font-bold shadow-lg transition-all cursor-pointer disabled:cursor-not-allowed ${
                    lowBalance
                      ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/25'
                      : 'bg-primary hover:bg-primary-hover text-white shadow-primary/25 disabled:opacity-50'
                  }`}
                >
                  {lowBalance ? (
                    <><i className="ri-vip-crown-line" /> Upgrade to generate</>
                  ) : (
                    <>
                      <i className="ri-sparkling-2-line" /> Generate
                      <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold">
                        {CREDITS} credit
                      </span>
                    </>
                  )}
                </button>
                <p className="text-xs text-dark-muted text-center">
                  {CREDITS} credit · you have {balance ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayoutV2>
  );
}
